import { useState } from 'react';
import { useAdmin } from '../AdminContext';
import { useCan } from '../useCan';
import { btnGhost, btnPrimary } from '../ui/styles';
import { choiceState } from '../../ui/styles';
import { TimeChoice } from './TimeChoice';
import { firstName } from './agendaRules';
import { FOLLOW_UP_WEEKS, bookingProblem, courseWarning, followUpDate } from '../../core/rules/staffPermissions';
import { DEFAULT_STAFF_ID } from '../../core/rules/agenda';
import { salonDayFor, staffList } from '../../core/rules/salonDays';
import { useRequestId } from '../../hooks/useRequestId';
import { dayMedium } from '../../core/format/dates';
import type { AgendaAppointment, AgendaBlock } from '../../core/types/agenda';
import type { AgendaControl } from './useAgenda';
type Props = {
  appointment: AgendaAppointment;
  appointments: AgendaAppointment[];
  blocks: AgendaBlock[];
  agenda: AgendaControl;
  onDone: (a: AgendaAppointment) => void;
  onCancel: () => void;
};

/**
 * "Plan vervolg" vanuit een afspraak: dezelfde klant en behandeling, dezelfde tijd over N weken
 * voorgesteld. Sarah overal; een collega alleen op haar eigen salondagen en voor behandelingen die
 * zij doet (core/rules/staffPermissions.ts, de server controleert het opnieuw).
 */
export function FollowUpForm({
  appointment: a,
  appointments,
  blocks,
  agenda,
  onDone,
  onCancel
}: Props) {
  const {
    site
  } = useAdmin();
  const can = useCan();
  const viewer = can.viewer('agenda.plan');
  const mayOverride = can.canAll('agenda.override');
  const team = staffList(site.staff);
  const [weeks, setWeeks] = useState<number>(4);
  const [date, setDate] = useState(() => followUpDate(a.date, 4));
  const [time, setTime] = useState(a.time);
  const [overlap, setOverlap] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const requestId = useRequestId(JSON.stringify([date, time, overlap]));
  const rules = {
    hours: site.openingHours,
    exceptions: site.salonExceptions,
    staff: team
  };
  const problem = date ? bookingProblem(viewer, {
    date,
    treatmentSlug: a.treatmentSlug
  }, rules) : null;
  const performer = viewer.isOwner ? salonDayFor(site.openingHours, site.salonExceptions, date)?.staffId ?? DEFAULT_STAFF_ID : viewer.staffId;
  const warning = a.session ? courseWarning(a.customer.kuur?.staffId, performer, team) : '';
  const canSubmit = Boolean(date && time) && !problem && !submitting;
  const pickWeeks = (w: number) => {
    setWeeks(w);
    setDate(followUpDate(a.date, w));
    setTime(a.time);
  };
  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const made = await agenda.add({
      requestId,
      treatmentSlug: a.treatmentSlug,
      date,
      time,
      staffId: performer,
      ...(a.customer.id ? {
        customerId: a.customer.id
      } : {
        guest: {
          name: a.customer.name,
          email: a.customer.email,
          phone: a.customer.phone
        }
      }),
      ...(overlap && mayOverride ? {
        allowOverlap: true
      } : {})
    });
    setSubmitting(false);
    if (made) onDone(made);
  };
  return <div className="space-y-4" data-id="element-408" data-mp="fbj319b4lpvi">
      <div data-id="element-409" data-mp="fahnarj60f7g">
        <h3 className="text-[15px] font-medium text-ink" data-id="element-410" data-mp="farhysf5najh">Vervolgafspraak</h3>
        <p className="mt-0.5 text-[13px] text-muted" data-id="element-411" data-mp="kavwicwi5v72">Zelfde behandeling voor {firstName(a.customer.name)}. Ze krijgt een bevestiging met agenda-uitnodiging.</p>
      </div>
      <div role="radiogroup" aria-label="Over hoeveel weken" className="flex flex-wrap gap-1.5" data-id="element-412" data-mp="b9xu1z1ppvql">
        {FOLLOW_UP_WEEKS.map(w => <button key={w} type="button" role="radio" aria-checked={weeks === w} onClick={() => pickWeeks(w)} className={`rounded-full px-3.5 py-1.5 text-[13px] ${choiceState(weeks === w)}`} data-id="element-413" data-mp="z0l3iora0tf9">
            {w} weken
          </button>)}
      </div>
      <TimeChoice date={date} onDate={setDate} time={time} onTime={setTime} durationMinutes={a.durationMinutes} appointments={appointments} blocks={blocks} overlap={overlap} onOverlap={setOverlap} data-id="element-414" data-mp="p7w10jdpepw2" />
      {problem && <p role="alert" className="text-[13px] text-clay" data-id="element-415" data-mp="1mqxfqbtk1cbn">
          {problem}
        </p>}
      {warning && !problem && <p className="rounded-md bg-sand px-3 py-2 text-[13px] text-ink" data-id="element-416" data-mp="3p931qaqnjal6">{warning}</p>}
      <div className="flex flex-wrap gap-2" data-id="element-417" data-mp="3l7zide9eawf7">
        <button type="button" onClick={() => void submit()} disabled={!canSubmit} aria-busy={submitting} className={btnPrimary} data-id="element-418" data-mp="3l8slm595b3sl">
          {submitting ? 'Inplannen…' : time && date ? `Inplannen op ${dayMedium(date)} ${time}` : 'Kies een tijd'}
        </button>
        <button type="button" onClick={onCancel} className={btnGhost} data-id="element-419" data-mp="csfy34qpcoca">
          Terug
        </button>
      </div>
    </div>;
}